/**
 * weather — the English half, typed against the German module, so a
 * missing or misspelled key is a type error in this file.
 */
import type { weather as de_weather } from '../de/weather';

export const weather: Record<keyof typeof de_weather, string> = {
  /* ATIS */
  'atis.station': 'Station and frequency',
  'atis.code': 'Code',
  'atis.rwy': 'Runway',
  'atis.tl': 'Transition level (TL)',
  'atis.zeit': 'Time UTC',
  'atis.wind': 'Wind',
  'atis.sicht': 'Visibility',
  'atis.wolken': 'Clouds',
  'atis.temp': 'Temperature/dew point',
  'atis.qnh': 'QNH',
  'atis.tendenz': 'Trend/further information',

  /* ---------------- METAR / TAF (ATIS rows and weather briefing) ---------------- */
  'wx.nostations':
    'No station: enter “von”, “nach” or the alternate as an ICAO code (e.g. LJLJ).',
  'wx.hint':
    'Filled from the METAR. Code, runway and TL come from ATIS only.',
  'wx.status.metar': 'METAR {id} {time}',
  'wx.status.taf': 'TAF valid until {time}',
  'wx.status.tafOf': 'TAF {id} valid until {time}',
  'wx.unavailable': 'No report for {ids} right now – what is in the form counts.',
  'wx.noroom': 'Both ATIS rows are taken – {ids} is missing. Clear a row.',
  'wx.nometar': 'no weather report for {id} – this aerodrome does not report.',
  'wx.nometar.brief': 'Official briefing:',
  'wx.nometar.generic':
    'no weather report for {id} – this aerodrome does not report. Use the national aviation weather service.',
  'wx.raw': 'Raw METAR/TAF',
  'wx.take.full': 'No room: {lines} no longer fits into the six lines. Clear a line.',
  'wx.field.derived': 'from the weather {id}',

  /* ---- the nearest station that does report (GET /api/wx/nearby) ---- */
  'wx.nearby.head': 'nearest report: {station} — {dist} NM {dir} of {of}',
  'wx.nearby.status': 'no METAR for {of} · nearest report {station} {dist} NM {dir}',
  // Prose that ends up on the printed form — written in the interface language at
  // write time, exactly like a reporting point's remark.
  'wx.nearby.cell': '{id} ({dist} NM from {of})',
  'wx.nearby.taken': 'In ATIS row {row}: {id}, not {of}.',
  'wx.nearby.noroom': 'No ATIS row free. Clear a row.',
  'wx.nearby.own': '{id} already has a row of its own on the form.',
  'wx.nearby.busy': 'looking for the nearest report …',
  'wx.dir.n': 'N',
  'wx.dir.ne': 'NE',
  'wx.dir.e': 'E',
  'wx.dir.se': 'SE',
  'wx.dir.s': 'S',
  'wx.dir.sw': 'SW',
  'wx.dir.w': 'W',
  'wx.dir.nw': 'NW',
  'wx.cat.vfr': 'VFR — ceiling ≥ 3,000 ft and visibility ≥ 8 km',
  'wx.cat.mvfr': 'MVFR — ceiling 1,000–3,000 ft or visibility 5–8 km',
  'wx.cat.ifr': 'IFR — ceiling 500–1,000 ft or visibility 1.6–5 km',
  'wx.cat.lifr': 'LIFR — ceiling < 500 ft or visibility < 1.6 km',
  'wx.taf.periods': 'TAF periods – graded VFR/MVFR/IFR, no VMC check:',
  'wx.taf.visib': 'visibility {v}',
  'wx.taf.visib.min': 'visibility ≥ {v}',
  'wx.taf.ceiling': 'ceiling {ft} ft',

  'wx.pane.title': 'Weather',

  /* ---------------- the weather view (panes/weather) ---------------- */
  'wx.decoded.title': 'Decoded from the METAR',
  'wx.row.wind': 'Wind',
  'wx.row.sicht': 'Visibility',
  'wx.row.wolken': 'Clouds',
  'wx.row.temp': 'Temp./dew',
  'wx.row.qnh': 'QNH',
  'wx.row.tendenz': 'Trend',
  'wx.role.von': 'From',
  'wx.role.nach': 'To',
  'wx.role.alt': 'Alternate',
  'wx.empty.title': 'No aerodrome entered.',
  'wx.empty.line': 'Enter “von” and “nach” as ICAO codes – the weather then arrives by itself.',
  'wx.card.pending': 'Fetching weather …',
  'wx.card.unavailable': 'No report right now – what is here counts.',
  'wx.card.atisrow': 'In ATIS row {n}',
  'wx.nometar.here': 'This aerodrome reports no weather.',
  'wx.nearby.title': 'Nearest report',
  'wx.nearby.dist': '{dist} NM {dir} of {of}',
  'wx.taf.valid': 'valid until {time}',
  'wx.taf.ateta': 'On arrival {time}',
  'wx.tick.etd': 'Departure',
  'wx.tick.eta': 'Arrival',
  'wx.sun.title': 'Daylight',
  'wx.sun.eta': 'Arrival {time}',
  'wx.sun.ss': 'Sunset {time}',
  'wx.sun.left': '{span} of daylight left',
  'wx.sun.after': '{span} after sunset',
};
